/**
 * PlaidConnectionMonitorPage.tsx
 *
 * VS-PLAID-CONNECTION-MONITORING: Connection Investigation Tool
 *
 * Integrated diagnostic tool combining connection failure monitoring with
 * real-time institution health status. Three-panel layout:
 * - Left: Failed connection cases
 * - Right-Top: Event timeline for selected case
 * - Right-Bottom: Auto-fetched institution health status
 *
 * Admin-only access via ProtectedRoute.
 */

import { FC, useState, useEffect, useCallback } from 'react';
import {
  FiAlertTriangle,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiSearch,
  FiHelpCircle,
  FiExternalLink,
  FiTag,
} from 'react-icons/fi';
import {
  listConnectionCases,
  getCaseDetails,
  updateCase,
  ConnectionCase,
  PlaidLinkEvent,
  UpdateCaseRequest,
  FiCheckMatch,
  FiCheckResponse,
} from '../services/plaidConnectionMonitorService';
import CaseSearchFilters, { FilterState } from '../components/plaid-monitoring/CaseSearchFilters';
import CaseSummaryStats from '../components/plaid-monitoring/CaseSummaryStats';
import CaseDetailsSummary from '../components/plaid-monitoring/CaseDetailsSummary';
import DiagnosticRecommendations from '../components/plaid-monitoring/DiagnosticRecommendations';
import ProductCompatibilityMatrix from '../components/plaid-monitoring/ProductCompatibilityMatrix';
import AdminMessaging, { MessagePayload, MessageRecord } from '../components/plaid-monitoring/AdminMessaging';
import PlaidSupportActions from '../components/plaid-monitoring/PlaidSupportActions';
import { sendMemberMessage, getCaseMessageHistory, saveMessageToLocalHistory } from '../services/notificationService';

// ─── API configuration ──────────────────────────────────────────────────────────────────────────
const INTEGRATIONS_API =
  import.meta.env.VITE_INTEGRATIONS_API_URL ||
  'https://vs-integrations-development.netlify.app';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

// Clear any stale cached token when this module loads (before component renders)
if (typeof sessionStorage !== 'undefined') {
  sessionStorage.removeItem('vs_admin_token');
  if (import.meta.env.DEV) console.log('[FI Investigation] Cleared stale token cache on module load');
}

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: FC }> = {
  open: { label: 'Open', color: 'text-orange-600 bg-orange-50', icon: FiAlertTriangle },
  investigating: { label: 'Investigating', color: 'text-blue-600 bg-blue-50', icon: FiClock },
  resolved: { label: 'Resolved', color: 'text-green-600 bg-green-50', icon: FiCheckCircle },
  closed_no_action: { label: 'Closed', color: 'text-gray-600 bg-gray-50', icon: FiXCircle },
};

const CLASSIFICATION_OPTIONS = [
  { value: 'member_credentials', label: 'Member Credentials' },
  { value: 'member_mfa', label: 'Member MFA' },
  { value: 'institution_degraded', label: 'Institution Degraded' },
  { value: 'institution_down', label: 'Institution Down' },
  { value: 'unknown', label: 'Unknown' },
];

// ─── Components ─────────────────────────────────────────────────────────────

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

const CaseCard: FC<{
  case: ConnectionCase;
  onSelect: (caseId: number) => void;
  isSelected: boolean;
}> = ({ case: c, onSelect, isSelected }) => {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm p-4 cursor-pointer transition-all ${
        isSelected ? 'ring-2 ring-blue-500' : 'hover:shadow-md'
      }`}
      onClick={() => onSelect(c.id)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-slate-700">Case #{c.id}</span>
            <StatusBadge status={c.status} />
            {c.priority === 'high' && (
              <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">High Priority</span>
            )}
          </div>

          {/* Member Information */}
          {c.member_name && (
            <div className="mb-2 pb-2 border-b border-slate-200">
              <div className="text-sm font-medium text-slate-700">{c.member_name}</div>
              {c.member_email && (
                <div className="text-xs text-slate-500">{c.member_email}</div>
              )}
              {c.member_auth0_id && (
                <div className="text-xs text-slate-400">Auth0: {c.member_auth0_id}</div>
              )}
            </div>
          )}

          {/* Institution */}
          {c.institution_name && (
            <div className="text-xs text-slate-600 mb-1">
              <span className="font-medium">Institution:</span> {c.institution_name}
            </div>
          )}

          <div className="text-xs text-slate-500">
            Classification: {c.classification || 'Unknown'}
          </div>
          <div className="text-xs text-slate-500">
            Impact: {c.member_impact || 'Unknown'}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400">
            {new Date(c.created_at).toLocaleDateString()}
          </div>
          <div className="text-xs text-slate-400">
            {new Date(c.created_at).toLocaleTimeString()}
          </div>
          {c.retry_attempt_number > 1 && (
            <div className="text-xs text-orange-600 mt-1 font-medium">
              Retry #{c.retry_attempt_number}
            </div>
          )}
        </div>
      </div>
      {c.plaid_link_sessions && (
        <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">
          <div>Flow: {c.plaid_link_sessions.flow_type}</div>
          <div>Outcome: {c.plaid_link_sessions.terminal_outcome}</div>
        </div>
      )}
    </div>
  );
};

const EventTimeline: FC<{ events: PlaidLinkEvent[] }> = ({ events }) => {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-700">Event Timeline</h3>
      {events.map((event, idx) => (
        <div key={event.id} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div
              className={`w-3 h-3 rounded-full ${
                event.event_name === 'ERROR'
                  ? 'bg-red-500'
                  : event.event_name === 'SELECT_INSTITUTION'
                  ? 'bg-blue-500'
                  : 'bg-gray-400'
              }`}
            />
            {idx < events.length - 1 && <div className="w-0.5 h-full bg-slate-200 flex-1 min-h-[20px]" />}
          </div>
          <div className="flex-1 pb-3">
            <div className="text-sm font-medium text-slate-700">{event.event_name}</div>
            <div className="text-xs text-slate-500">
              {new Date(event.event_timestamp).toLocaleString()}
            </div>
            {event.institution_name && (
              <div className="text-xs text-slate-600 mt-1">Institution: {event.institution_name}</div>
            )}
            {event.error_code && (
              <div className="mt-1 text-xs">
                <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">{event.error_code}</span>
                {event.error_message_sanitized && (
                  <div className="text-xs text-slate-600 mt-1">{event.error_message_sanitized}</div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Institution Health Card ────────────────────────────────────────────────────────────────────
const HealthStatusPill: FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { cls: string; icon: FC; label: string }> = {
    healthy: { cls: 'bg-green-100 text-green-800', icon: FiCheckCircle, label: 'Healthy' },
    degraded: { cls: 'bg-amber-100 text-amber-800', icon: FiAlertTriangle, label: 'Degraded' },
    down: { cls: 'bg-red-100 text-red-800', icon: FiXCircle, label: 'Down' },
    unknown: { cls: 'bg-slate-100 text-slate-600', icon: FiHelpCircle, label: 'Unknown' },
  };
  const s = map[status] || map.unknown;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${s.cls}`}>
      <Icon size={12} />
      {s.label}
    </span>
  );
};

// InstitutionHealthCard component removed - use FI search results instead

type SearchType = 'name' | 'routing' | 'institution_id' | 'url';

/**
 * Extract institution name from URL
 * Examples:
 * - https://www.missionlane.com/ → "mission lane"
 * - https://www.chase.com → "chase"
 * - https://www.bankofamerica.com → "bank of america"
 */
function extractInstitutionNameFromUrl(url: string): string {
  try {
    // Remove protocol and www
    let domain = url.replace(/^https?:\/\/(www\.)?/i, '');

    // Remove trailing slash and path
    domain = domain.split('/')[0];

    // Remove TLD (.com, .org, etc.)
    domain = domain.replace(/\.(com|org|net|edu|gov|io|co|bank)$/i, '');

    // Handle special cases
    const specialCases: Record<string, string> = {
      'missionlane': 'mission lane',
      'bankofamerica': 'bank of america',
      'wellsfargo': 'wells fargo',
      'usbank': 'us bank',
      'pnc': 'pnc bank',
      'tdbank': 'td bank',
      'capitalone': 'capital one'
    };

    const normalized = domain.toLowerCase();
    if (specialCases[normalized]) {
      return specialCases[normalized];
    }

    // Insert spaces before capital letters for camelCase
    domain = domain.replace(/([a-z])([A-Z])/g, '$1 $2');

    return domain.toLowerCase().trim();
  } catch (e) {
    return url; // Fallback to original URL if parsing fails
  }
}

const PlaidConnectionMonitorPage: FC = () => {
  // FI Search state
  const [fiSearchQuery, setFiSearchQuery] = useState('');
  const [fiSearchType, setFiSearchType] = useState<SearchType>('name');
  const [fiSearchLoading, setFiSearchLoading] = useState(false);
  const [fiSearchError, setFiSearchError] = useState<string | null>(null);
  const [fiSearchResult, setFiSearchResult] = useState<FiCheckResponse | null>(null);
  const [showProductMatrix, setShowProductMatrix] = useState<string | null>(null); // institution_id of expanded matrix

  // Connection Investigation state
  const [cases, setCases] = useState<ConnectionCase[]>([]);
  const [totalCases, setTotalCases] = useState(0);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [selectedCaseEvents, setSelectedCaseEvents] = useState<PlaidLinkEvent[]>([]);
  const [allCaseEvents, setAllCaseEvents] = useState<Map<number, PlaidLinkEvent[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [institutionUrlCache, setInstitutionUrlCache] = useState<Map<string, string>>(new Map());

  // Messaging state
  const [messageHistory, setMessageHistory] = useState<MessageRecord[]>([]);

  // Search/Filter state
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    status: '',
    classification: '',
    institutionName: '',
    dateFrom: '',
    dateTo: ''
  });

  // FI Search function - supports multiple search types
  const runFiSearch = useCallback(async () => {
    setFiSearchError(null);
    setFiSearchResult(null);
    const q = fiSearchQuery.trim();

    // Validation based on search type
    if (!q) {
      const typeLabels = {
        name: 'institution name',
        routing: 'routing number',
        institution_id: 'institution ID',
        url: 'institution URL'
      };
      setFiSearchError(`Enter a ${typeLabels[fiSearchType]} to search.`);
      return;
    }

    // Additional validation for routing number (must be 9 digits)
    if (fiSearchType === 'routing' && !/^\d{9}$/.test(q.replace(/\s/g, ''))) {
      setFiSearchError('Routing number must be exactly 9 digits.');
      return;
    }

    const params = new URLSearchParams();

    // Set appropriate query parameter based on search type
    if (fiSearchType === 'name') {
      params.set('query', q);
    } else if (fiSearchType === 'routing') {
      params.set('routing_numbers', q);
    } else if (fiSearchType === 'institution_id') {
      // For institution_id, we'll search by name first to get the ID
      // (Plaid's institutionsGetById doesn't support search by ID directly in this context)
      params.set('query', q);
    } else if (fiSearchType === 'url') {
      // Extract institution name from URL and search by name
      const extractedName = extractInstitutionNameFromUrl(q);
      params.set('query', extractedName);
      console.log(`[FI Search] URL "${q}" → searching for "${extractedName}"`);
    }

    params.set('includeAuth', 'true');

    setFiSearchLoading(true);
    try {
      const res = await fetch(
        `${INTEGRATIONS_API}/plaid/institution-check?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'VS-Client-Id': VS_CLIENT_ID,
            'Content-Type': 'application/json'
          }
        }
      );
      const body = await res.json();
      if (!res.ok || body?.status === 'fail') {
        throw new Error(body?.data?.reason || body?.message || `Request failed (${res.status})`);
      }
      setFiSearchResult(body.data as FiCheckResponse);
    } catch (e) {
      setFiSearchError(e instanceof Error ? e.message : String(e));
    } finally {
      setFiSearchLoading(false);
    }
  }, [fiSearchQuery, fiSearchType]);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filterParams = {
        status: filters.status || undefined,
        classification: filters.classification || undefined,
        searchQuery: filters.searchQuery || undefined,
        institutionName: filters.institutionName || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      };
      const response = await listConnectionCases(50, 0, filterParams);
      setCases(response.data.cases);
      setTotalCases(response.data.pagination.total);

      // Fetch events for all cases to display in Case Details Summary
      const eventsMap = new Map<number, PlaidLinkEvent[]>();
      await Promise.all(
        response.data.cases.map(async (c) => {
          try {
            const details = await getCaseDetails(c.id);
            eventsMap.set(c.id, details.data.events);
          } catch (err) {
            console.error(`Failed to fetch events for case ${c.id}:`, err);
            eventsMap.set(c.id, []);
          }
        })
      );
      setAllCaseEvents(eventsMap);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const handleApplyFilters = useCallback(() => {
    loadCases();
  }, [loadCases]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      searchQuery: '',
      status: '',
      classification: '',
      institutionName: '',
      dateFrom: '',
      dateTo: ''
    });
  }, []);

  const handleSelectCase = useCallback(async (caseId: number) => {
    setSelectedCaseId(caseId);
    setLoading(true);
    setError(null);

    try {
      // Fetch case details and events
      const response = await getCaseDetails(caseId);
      setSelectedCaseEvents(response.data.events);

      // Find institution info from case
      const selectedCase = cases.find(c => c.id === caseId);
      const selectEvent = response.data.events.find(
        (e) => e.event_name === 'SELECT_INSTITUTION' && (e.institution_id || e.institution_name)
      );

      // Auto-populate Institution Lookup with institution name for manual search
      const institutionName = selectedCase?.institution_name || selectEvent?.institution_name;
      if (institutionName) {
        setFiSearchQuery(institutionName);
        setFiSearchType('name');
        setFiSearchResult(null);
        setFiSearchError(null);
      }

      // Fetch institution URL for Plaid support ticket (if we have institution_id)
      const institutionId = selectEvent?.institution_id;
      if (institutionId && !institutionUrlCache.has(institutionId)) {
        try {
          const params = new URLSearchParams();
          params.set('query', institutionId);
          params.set('includeAuth', 'true');

          const fiRes = await fetch(
            `${INTEGRATIONS_API}/plaid/institution-check?${params.toString()}`,
            {
              method: 'GET',
              headers: {
                'VS-Client-Id': VS_CLIENT_ID,
                'Content-Type': 'application/json'
              }
            }
          );
          const fiBody = await fiRes.json();

          if (fiRes.ok && fiBody?.data?.matches?.length > 0) {
            const url = fiBody.data.matches[0].url;
            if (url) {
              setInstitutionUrlCache(new Map(institutionUrlCache.set(institutionId, url)));
            }
          }
        } catch (err) {
          console.error('Failed to fetch institution URL:', err);
          // Don't fail the whole case load if URL fetch fails
        }
      }

      // Load message history for this case
      const history = await getCaseMessageHistory(caseId);
      setMessageHistory(history);

    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [cases]);

  // Handle sending messages
  const handleSendMessage = useCallback(async (payload: MessagePayload) => {
    try {
      const result = await sendMemberMessage(payload);

      if (result.success) {
        // Save to local history
        saveMessageToLocalHistory(payload.caseId, {
          caseId: payload.caseId,
          channel: payload.channel,
          subject: payload.subject,
          body: payload.body,
          sentAt: new Date().toISOString(),
          sentBy: 'admin', // TODO: Get from auth context
          status: 'sent'
        });

        // Reload message history
        const history = await getCaseMessageHistory(payload.caseId);
        setMessageHistory(history);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  }, []);

  // Search institution from case
  const handleSearchInstitutionFromCase = useCallback(() => {
    const selectEvent = selectedCaseEvents.find(
      (e) => e.event_name === 'SELECT_INSTITUTION' && e.institution_name
    );
    if (selectEvent?.institution_name) {
      setFiSearchQuery(selectEvent.institution_name);
      setFiSearchResult(null);
      setFiSearchError(null);
      // Auto-run search
      setTimeout(() => {
        const q = selectEvent.institution_name!.trim();
        if (!q) return;
        const params = new URLSearchParams();
        params.set('query', q);
        params.set('includeAuth', 'true');
        setFiSearchLoading(true);
        fetch(
          `${INTEGRATIONS_API}/plaid/institution-check?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'VS-Client-Id': VS_CLIENT_ID,
              'Content-Type': 'application/json'
            }
          }
        )
          .then(res => res.json())
          .then(body => {
            if (body?.status === 'fail') throw new Error(body?.data?.reason);
            setFiSearchResult(body.data as FiCheckResponse);
          })
          .catch(e => setFiSearchError(e instanceof Error ? e.message : String(e)))
          .finally(() => setFiSearchLoading(false));
      }, 100);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedCaseEvents]);

  // Smart classification helpers removed - use manual classification dropdown in case list

  return (
    <div style={{ backgroundColor: '#EAEDF4' }}>
      <main className="max-w-7xl mx-auto px-4 py-6 pb-24">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-700 mb-2 flex items-center gap-2">
            <FiSearch size={28} />
            FI Investigation
          </h1>
          <p className="text-base text-slate-500">
            Search institution health status and diagnose connection failures
          </p>
        </div>

        {/* ===== TOP SECTION: FI Search ===== */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 min-h-[120px]">
          <h2 className="text-lg font-semibold text-slate-700 mb-4">Institution Lookup</h2>

          {/* Search Type Selector */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-slate-600 mb-2">Search By</label>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFiSearchType('name')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  fiSearchType === 'name'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Institution Name
              </button>
              <button
                onClick={() => setFiSearchType('url')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  fiSearchType === 'url'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                URL
              </button>
              <button
                onClick={() => setFiSearchType('routing')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  fiSearchType === 'routing'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Routing Number
              </button>
              <button
                onClick={() => setFiSearchType('institution_id')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  fiSearchType === 'institution_id'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Institution ID
              </button>
            </div>
          </div>

          {/* Search Input */}
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={fiSearchQuery}
              onChange={(e) => setFiSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runFiSearch()}
              placeholder={
                fiSearchType === 'name'
                  ? 'Search by institution name (e.g., Chase, Wells Fargo)'
                  : fiSearchType === 'url'
                  ? 'Enter institution URL (e.g., https://www.chase.com)'
                  : fiSearchType === 'routing'
                  ? 'Enter 9-digit routing number (e.g., 021000021)'
                  : 'Enter Plaid institution ID (e.g., ins_3)'
              }
              className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={runFiSearch}
              disabled={fiSearchLoading}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2 whitespace-nowrap"
            >
              <FiSearch size={16} />
              {fiSearchLoading ? 'Searching...' : 'Search'}
            </button>
            {(fiSearchQuery || fiSearchResult) && (
              <button
                onClick={() => {
                  setFiSearchQuery('');
                  setFiSearchResult(null);
                  setFiSearchError(null);
                }}
                className="px-6 py-2.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 font-medium flex items-center gap-2 whitespace-nowrap"
              >
                <FiXCircle size={16} />
                Clear
              </button>
            )}
          </div>

          {/* FI Search Error */}
          {fiSearchError && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {fiSearchError}
            </div>
          )}

          {/* FI Search Results */}
          {fiSearchResult && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <strong>{fiSearchResult.match_count}</strong> match{fiSearchResult.match_count !== 1 ? 'es' : ''}
                {fiSearchResult.supported ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    Supported
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                    Not Supported
                  </span>
                )}
              </div>
              {fiSearchResult.matches.map((match) => {
                const authMethods = match.auth_metadata?.supported_methods || {};
                const requiredProducts = ['transactions', 'auth', 'identity', 'balance'];
                const unsupportedProducts = requiredProducts.filter(p => !match.products.includes(p));

                return (
                  <div key={match.institution_id} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
                    <div className="flex items-start gap-4 mb-4">
                      {match.logo ? (
                        <img
                          src={`data:image/png;base64,${match.logo}`}
                          alt={`${match.name} logo`}
                          className="w-16 h-16 rounded border border-slate-200 object-contain flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded bg-slate-200 flex-shrink-0 flex items-center justify-center text-slate-400 text-xs font-semibold">
                          {match.name.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-slate-800">{match.name}</h3>
                          <HealthStatusPill status={match.health_status} />
                        </div>
                        <div className="text-xs text-slate-500 font-mono mb-2">{match.institution_id}</div>

                        {/* OAuth Alert */}
                        {match.oauth && (
                          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg text-xs font-medium text-orange-800 mb-2">
                            <FiAlertTriangle size={12} />
                            OAuth Required - No username/password
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Authentication Methods */}
                    <div className="mb-3 pb-3 border-b border-slate-200">
                      <h4 className="text-xs font-semibold text-slate-700 mb-2">Authentication</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">OAuth:</span>
                          <span className={match.oauth ? 'text-orange-700 font-semibold' : 'text-green-700'}>
                            {match.oauth ? 'Required' : 'Not Required'}
                          </span>
                        </div>
                        {authMethods.instant_auth !== undefined && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">Instant Auth:</span>
                            {authMethods.instant_auth ? (
                              <FiCheckCircle className="text-green-600" size={14} />
                            ) : (
                              <FiXCircle className="text-slate-400" size={14} />
                            )}
                          </div>
                        )}
                        {authMethods.instant_match !== undefined && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">Instant Match:</span>
                            {authMethods.instant_match ? (
                              <FiCheckCircle className="text-green-600" size={14} />
                            ) : (
                              <FiXCircle className="text-slate-400" size={14} />
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Product Support */}
                    <div className="mb-3 pb-3 border-b border-slate-200">
                      <h4 className="text-xs font-semibold text-slate-700 mb-2">
                        Product Support ({match.products.length} available)
                      </h4>
                      {unsupportedProducts.length > 0 ? (
                        <div className="bg-red-50 border border-red-200 px-3 py-2 rounded-lg text-xs text-red-700 mb-2">
                          <strong>⚠️ Missing:</strong> {unsupportedProducts.join(', ')}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg mb-2">
                          <FiCheckCircle size={14} />
                          All required products supported
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {match.products.slice(0, 8).map((p) => (
                          <span key={p} className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                            {p}
                          </span>
                        ))}
                        {match.products.length > 8 && (
                          <span className="px-2 py-1 bg-slate-200 text-slate-600 rounded text-xs font-medium">
                            +{match.products.length - 8} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Routing Numbers */}
                    {match.routing_numbers && match.routing_numbers.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold text-slate-700 mb-2">
                          Routing Numbers ({match.routing_numbers.length} registered)
                        </h4>
                        {match.routing_numbers.length <= 3 ? (
                          <div className="flex flex-wrap gap-2">
                            {match.routing_numbers.map((rn) => (
                              <span key={rn} className="px-2 py-1 bg-slate-100 text-slate-700 rounded font-mono text-xs">
                                {rn}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-600">
                            Sample: {match.routing_numbers.slice(0, 3).join(', ')}...
                          </div>
                        )}
                      </div>
                    )}

                    {/* Product Compatibility Matrix Toggle */}
                    <div className="pt-3 border-t border-slate-200">
                      <button
                        onClick={() => setShowProductMatrix(
                          showProductMatrix === match.institution_id ? null : match.institution_id
                        )}
                        className="w-full flex items-center justify-between px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors"
                      >
                        <span>View Full Product Compatibility Matrix</span>
                        <span className="text-xs">{showProductMatrix === match.institution_id ? '▼' : '▶'}</span>
                      </button>

                      {showProductMatrix === match.institution_id && (
                        <div className="mt-3">
                          <ProductCompatibilityMatrix
                            supportedProducts={match.products}
                            requiredProducts={['transactions', 'auth', 'identity', 'balance']}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Institution Diagnostics for Search Results */}
          {fiSearchResult && fiSearchResult.matches.length > 0 && (
            <div className="mt-6">
              <DiagnosticRecommendations
                institution={fiSearchResult.matches[0]}
                events={[]}
              />
            </div>
          )}
        </div>

        {/* ===== SUMMARY STATS ===== */}
        <CaseSummaryStats
          totalCases={totalCases}
          openCount={cases.filter(c => c.status === 'open').length}
          investigatingCount={cases.filter(c => c.status === 'investigating').length}
          resolvedCount={cases.filter(c => c.status === 'resolved').length}
        />

        {/* ===== CASE DETAILS SUMMARY ===== */}
        {cases.length > 0 && (
          <CaseDetailsSummary
            cases={cases}
            caseEvents={allCaseEvents}
          />
        )}

        {/* ===== SEARCH & FILTER ===== */}
        <CaseSearchFilters
          filters={filters}
          onFilterChange={setFilters}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          isLoading={loading}
        />

        {/* ===== BOTTOM SECTION: Connection Investigation ===== */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-700">Connection Failures</h2>
              <p className="text-sm text-slate-500">Failed Plaid Link connection attempts</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">
                {cases.length} case{cases.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={loadCases}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <FiRefreshCw className={loading ? 'animate-spin' : ''} size={16} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* Two-column layout for cases */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Case list */}
            <div className="space-y-3">
              {loading && cases.length === 0 ? (
                <div className="bg-slate-50 rounded-lg p-8 text-center text-slate-500">Loading cases...</div>
              ) : cases.length === 0 ? (
                <div className="bg-slate-50 rounded-lg p-8 text-center text-slate-500">No cases found</div>
              ) : (
                cases.map((c) => (
                  <CaseCard
                    key={c.id}
                    case={c}
                    onSelect={handleSelectCase}
                    isSelected={selectedCaseId === c.id}
                  />
                ))
              )}
            </div>

            {/* Right: Case details + Institution health */}
            <div className="lg:sticky lg:top-6 lg:self-start space-y-4">
              {/* Event Timeline */}
              {selectedCaseId ? (
                <div className="bg-slate-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-slate-700">
                      Case #{selectedCaseId} Timeline
                    </h3>
                    {selectedCaseEvents.find(e => e.event_name === 'SELECT_INSTITUTION' && e.institution_name) && (
                      <button
                        onClick={handleSearchInstitutionFromCase}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                      >
                        <FiSearch size={12} />
                        Check FI Health Above ↑
                      </button>
                    )}
                  </div>
                  {loading ? (
                    <div className="text-center py-12 text-slate-500">Loading events...</div>
                  ) : selectedCaseEvents.length > 0 ? (
                    <EventTimeline events={selectedCaseEvents} />
                  ) : (
                    <div className="text-center py-12 text-slate-500">No events found</div>
                  )}
                </div>
              ) : (
                <div className="bg-slate-50 rounded-lg p-8 text-center text-slate-500">
                  Select a case to view timeline
                </div>
              )}

              {/* Institution Health removed - use FI Search instead for institution diagnostics */}

              {/* Plaid Support Actions - Always available for any case */}
              {selectedCaseId && selectedCaseEvents.length > 0 && (() => {
                const selectedCase = cases.find(c => c.id === selectedCaseId);
                const errorEvent = selectedCaseEvents.find(e => e.error_code);

                if (!selectedCase) return null;

                // Determine if this is a critical institution error
                const criticalErrors = [
                  'INSTITUTION_NO_LONGER_SUPPORTED',
                  'INSTITUTION_NOT_FOUND',
                  'INSTITUTION_ERROR',
                  'INSTITUTION_NOT_RESPONDING'
                ];
                const isCriticalInstitutionError = errorEvent?.error_code &&
                  criticalErrors.includes(errorEvent.error_code);

                // Show for critical errors OR if classification is institution-related
                const shouldAutoShow = isCriticalInstitutionError ||
                  selectedCase.classification?.includes('institution');

                const institutionId = errorEvent?.institution_id || null;
                const institutionUrl = institutionId ? institutionUrlCache.get(institutionId) || null : null;

                return (
                  <PlaidSupportActions
                    selectedCase={selectedCase}
                    events={selectedCaseEvents}
                    session={selectedCase.plaid_link_sessions}
                    errorCode={errorEvent?.error_code || null}
                    institutionId={institutionId}
                    institutionUrl={institutionUrl}
                  />
                );
              })()}

              {/* Admin Messaging */}
              {selectedCaseId && (
                <AdminMessaging
                  selectedCase={cases.find(c => c.id === selectedCaseId) || null}
                  onSendMessage={handleSendMessage}
                  messageHistory={messageHistory}
                />
              )}

              {/* Smart Classification Actions removed - use manual classification in case list */}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PlaidConnectionMonitorPage;
